/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionNotifyUsers';

const ActionNotifyUsersManager: ActionManagerFn<'ActionNotifyUsers'> = (
  platform: Platform,
): ActionManager<'ActionNotifyUsers'> => {
  return {
    ...baseActionManager(platform, 'ActionNotifyUsers'),
    defaultValue,
    props: {
      ...baseActionProps,
      userNames: {
        label: 'User Names',
        type: 'expression',
        returnType: async () => 'any',
        helpText:
          "Usernames to be notified. e.g. 'admin' or ['admin', 'someone@example.com'].",
        required: true,
      },
      subject: {
        label: 'Subject',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Subject of the notification.',
        required: true,
      },
      body: {
        label: 'Body',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Body of the notification. (Markdown supported)',
        required: true,
      },
    },
    name: 'Notify Users',
    icon: { iconName: 'comment-exclamation' },
    replaceableWith: ['ActionNotifyRoles'],
    description: `**Overview**<br/>
The Notify Users action is a targeted communication component designed to send alerts or messages to specific individuals within the application. Unlike the broader "Notify Roles" action which broadcasts to groups, this component allows for precise, direct messaging to one or more identified users.<br/><br/>
This tool is essential for personalized workflows where specific individuals need to be informed of events relevant to them personally. It supports rich text formatting via Markdown for the message body and offers flexible targeting options, accepting either a single username/email or a dynamic list of recipients.

**Key Features**<br/>
* **Precision Targeting:** Delivers messages exclusively to the specified user(s), ensuring privacy and relevance.
* **Flexible Input:** The userNames property accepts both a single string (e.g., 'john.doe') and an array of strings (e.g., ['john.doe', 'jane.smith@company.com']), facilitating both 1-on-1 and small group messaging.
* **Rich Content:** Explicitly supports Markdown in the notification body, allowing developers to structure messages with headers, bold text, bullet points, or clickable links.
* **Component Interchangeability:** Defined as replaceable with ActionNotifyRoles, allowing builders to easily switch between individual targeting and role-based broadcasting as requirements evolve.

**Properties**<br/>
* **User Names (Required):** An expression property returning any (String or Array).
  * *Function:* Specifies the unique identifiers (usernames or email addresses) of the recipients.
* **Subject (Required):** An expression property returning a string.
  * *Function:* The headline or subject line of the notification.
* **Body (Required):** An expression property returning a string.
  * *Function:* The main content of the message. Supports Markdown formatting for better readability.

**Use Cases**<br/>
* **Task Assignment:** Notifying a specific user immediately when a task is assigned to them via a "Delegate" button.
* **Status Updates:** Informing a requestor that their specific leave application has been "Approved" or "Rejected."
* **Mentions:** Triggering a notification when a user is specifically @mentioned in a comment thread.`,
  };
};

export default ActionNotifyUsersManager;
